// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Codex device-code sign-in. Shown in the connect modal, the onboarding
// gate and the Konvo panel; it takes its colours from the theme, so the host decides
// the surface.
import styles from '@app/features/konvo_connections/components/KonvoCodexSignIn.module.css';
import {useKonvoCodexLink} from '@app/features/konvo_connections/hooks/useKonvoCodexLink';
import type {KonvoCodexStatus} from '@app/features/konvo_connections/state/KonvoConnections';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {OpenAILogo} from '@app/features/ui/components/icons/OpenAILogo';
import {Spinner} from '@app/features/ui/components/Spinner';
import {ArrowSquareOutIcon, CheckIcon, CopyIcon} from '@app/features/ui/icons/Icons';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {useState} from 'react';
export function KonvoCodexSignIn({onConnected}: {onConnected: (status: KonvoCodexStatus) => void}) {
	const {i18n} = useLingui();
	const {login, starting, error, start, check} = useKonvoCodexLink(onConnected);
	const [copied, setCopied] = useState(false),
		[openError, setOpenError] = useState(false);
	const copy = (code: string) => void TextCopyCommands.copy(i18n, code, true).then(setCopied);
	return (
		<div className={styles.steps} data-flx="konvo-codex-signin">
			<div className={styles.step}>
				<span className={styles.number} aria-hidden="true">
					1
				</span>
				<span className={styles.stepText}>
					<strong className={styles.stepTitle}>
						<Trans>Copy your one-time code</Trans>
					</strong>
					<span className={styles.stepHint}>
						<Trans>You’ll enter this code on OpenAI’s sign-in page.</Trans>
					</span>
				</span>
				{(login || starting) && (
					<div className={clsx(styles.stepAction, styles.codeBox)}>
						{login ? (
							<>
								<code data-flx="konvo-codex-signin.code">{login.user_code}</code>
								<button
									type="button"
									className={clsx(styles.copy, copied && styles.copied)}
									onClick={() => copy(login.user_code)}
									data-flx="konvo-codex-signin.copy"
								>
									{copied ? <CheckIcon size={16} aria-hidden="true" /> : <CopyIcon size={16} aria-hidden="true" />}
									{copied ? <Trans>Copied</Trans> : <Trans>Copy code</Trans>}
								</button>
							</>
						) : (
							<span className={styles.codePending} role="status">
								<Spinner size="small" />
								<Trans>Getting your sign-in code…</Trans>
							</span>
						)}
					</div>
				)}
			</div>
			<div className={styles.step}>
				<span className={styles.number} aria-hidden="true">
					2
				</span>
				<span className={styles.stepText}>
					<strong className={styles.stepTitle}>
						<Trans>Sign in with your ChatGPT account</Trans>
					</strong>
					<span className={styles.stepHint}>
						<Trans>
							Use the account you want to power your assistant. If asked, enable device-code sign-in in your ChatGPT
							security settings.
						</Trans>
					</span>
				</span>
				<button
					type="button"
					className={clsx(styles.stepAction, styles.open)}
					disabled={!login || starting}
					onClick={() => {
						if (!login) return;
						// Copy on the way out too, so the code is ready to paste on OpenAI's page.
						if (!copied) copy(login.user_code);
						void openExternalUrl(login.verification_url).then(
							() => setOpenError(false),
							() => setOpenError(true),
						);
					}}
					data-flx="konvo-codex-signin.open"
				>
					<OpenAILogo size={18} />
					<Trans>Open OpenAI sign-in</Trans>
					<ArrowSquareOutIcon size={16} aria-hidden="true" />
				</button>
			</div>
			{openError && (
				<p className={styles.error} role="alert">
					<Trans>Could not open your browser. Try the sign-in button again.</Trans>
				</p>
			)}
			{login && (
				<p className={styles.status} role="status">
					<Spinner size="small" />
					<Trans>Waiting for approval. This step completes automatically when you’re signed in.</Trans>
				</p>
			)}
			{error && (
				<p className={styles.error} role="alert">
					{error}
				</p>
			)}
			{!starting && (
				<div className={styles.links}>
					{login && (
						<button type="button" className={styles.textButton} onClick={() => void check()}>
							<Trans>I’ve signed in — check again</Trans>
						</button>
					)}
					<button
						type="button"
						className={styles.textButton}
						onClick={() => {
							setCopied(false);
							void start();
						}}
					>
						<Trans>Get a new code</Trans>
					</button>
				</div>
			)}
		</div>
	);
}
